import { daysLeft, safeZone, urgencyOf } from '../raise-figures';
import {
  cardOf,
  indexState,
  sortFrom,
  withSort,
  type ColumnView,
  type DotView,
  type ListScope,
  type LiveRaise,
} from '../raises';
import { $, $$, attr, text } from './dom';
import { reducedMotion } from './motion';
import { timeZone } from './widgets';

/** What the page reads from the world, for a test to stand in. */
export interface RaisesDeps {
  now: () => number;
  /** The reader's IANA zone, or null where the browser will not say. */
  zone: () => string | null;
}

const DEFAULT_DEPS: RaisesDeps = {
  now: () => Date.now(),
  zone: timeZone,
};

/**
 * The live raises index (views/raises.hbs, /raises). The server drew it in
 * London's zone with the address's sort; this works it out again in the
 * reader's zone (indexState, the same function the server ran), runs the
 * sort without a reload, keeping it in the address, and ties each timeline
 * dot to its card: pointing at or focusing either one marks both and names
 * the raise under the timeline. It returns a function that stops the hero's
 * app panel taking turns.
 */
export function raisesPage(
  root: HTMLElement,
  deps: RaisesDeps = DEFAULT_DEPS,
): () => void {
  const panel = $(root, '[data-rx-panel]');
  const stop = panel ? appPanel(panel) : () => {};
  let data: { raises: LiveRaise[]; scope: ListScope | null };
  try {
    data = JSON.parse($(root, '#rx-data')?.textContent ?? '') as typeof data;
  } catch {
    return stop;
  }
  const { raises, scope } = data;
  const zone = safeZone(deps.zone());
  if (panel) paintReminder(panel, new Date(deps.now()), zone);
  const byId = new Map(raises.map((r) => [r.id, r]));
  const cards = new Map(
    $$(root, '[data-rx-card]').map((li) => [li.dataset.rxCard ?? '', li]),
  );
  const list = $(root, '[data-rx-cards]');
  const caption = $(root, '[data-rx-caption]');
  const query = new URL(window.location.href).searchParams;
  let sort = sortFrom(query.get('sort'));
  let active: string | null = null;

  const mark = (id: string | null) => {
    active = id;
    for (const [cardId, li] of cards) {
      li.classList.toggle('is-active', cardId === id);
    }
    for (const dot of $$(root, '[data-rx-dot]')) {
      dot.classList.toggle('is-active', dot.dataset.rxDot === id);
    }
    const dot = id ? $(root, `[data-rx-dot="${id}"]`) : null;
    caption?.classList.toggle('is-on', Boolean(dot));
    text(caption, dot?.dataset.caption ?? caption?.dataset.idle ?? '');
  };

  const draw = () => {
    const now = new Date(deps.now());
    const state = indexState(raises, { now, zone, sort, scope });
    const shown = new Set(state.shown.map((r) => r.id));
    for (const r of state.shown) {
      const li = cards.get(r.id);
      if (!li) continue;
      li.hidden = false;
      list?.append(li);
      paintCard(li, cardOf(r, now, zone));
    }
    for (const [id, li] of cards) if (!shown.has(id)) li.hidden = true;
    const empty = $(root, '[data-rx-empty]');
    if (empty) empty.hidden = state.shown.length > 0;
    text($(root, '[data-rx="count"]'), state.count);
    text($(root, '[data-rx="range"]'), state.range);
    paintTimeline(root, state.columns);

    for (const b of $$<HTMLButtonElement>(root, '[data-rx-sort] button')) {
      attr(b, 'aria-pressed', String(b.value === sort));
    }
    mark(active && shown.has(active) ? active : null);
  };

  const remember = () => {
    // The page's own address, its filters and utm_* kept: only the sort moves.
    const url = `${window.location.pathname}${withSort(window.location.search, sort)}${window.location.hash}`;
    window.history.replaceState(window.history.state, '', url);
  };

  // The sort is a GET form for a page without its script; with it, a press
  // reorders the list in place.
  for (const b of $$<HTMLButtonElement>(root, '[data-rx-sort] button')) {
    b.addEventListener('click', (e) => {
      e.preventDefault();
      sort = sortFrom(b.value);
      draw();
      remember();
    });
  }

  const target = (node: EventTarget | null) =>
    node instanceof Element
      ? node.closest<HTMLElement>('[data-rx-dot], [data-rx-card]')
      : null;
  const idOf = (el: HTMLElement | null) =>
    el ? (el.dataset.rxDot ?? el.dataset.rxCard ?? null) : null;
  const enter = (e: Event) => {
    const id = idOf(target(e.target));
    if (id && byId.has(id)) mark(id);
  };
  const leave = (e: Event) => {
    const from = idOf(target(e.target));
    const to = idOf(target((e as MouseEvent | FocusEvent).relatedTarget));
    if (from && from !== to) mark(to);
  };
  root.addEventListener('mouseover', enter);
  root.addEventListener('mouseout', leave);
  root.addEventListener('focusin', enter);
  root.addEventListener('focusout', leave);

  // A dot's press brings its card into view.
  root.addEventListener('click', (e) => {
    const dot = (e.target as Element).closest<HTMLElement>('[data-rx-dot]');
    const li = dot ? cards.get(dot.dataset.rxDot ?? '') : undefined;
    if (!li) return;
    const still = window.matchMedia?.(
      '(prefers-reduced-motion: reduce)',
    ).matches;
    li.scrollIntoView?.({
      behavior: still ? 'auto' : 'smooth',
      block: 'center',
    });
  });

  draw();
  return stop;
}

/** How long each of the app panel's rows stays open (.rx-feat-prog's 4.2s). */
export const PANEL_MS = 4200;

/**
 * The hero's app panel (.rx-ap): its rows open in turn, every PANEL_MS, with
 * the line along the open one's foot timing it (.is-auto). Pointing at or
 * focusing a row opens it and holds it there, the line gone, until the
 * pointer leaves the panel or focus leaves it. For a reader who asks for
 * less motion the first row stays open and nothing turns, but pointing
 * still opens a row. Returns a function that stops it.
 */
export function appPanel(root: HTMLElement): () => void {
  const rows = $$<HTMLButtonElement>(root, '[data-rx-feat]');
  if (rows.length === 0) return () => {};
  const still = reducedMotion();
  let open = Math.max(
    0,
    rows.findIndex((row) => row.classList.contains('is-on')),
  );
  let timer: number | undefined;
  let pointing = false;

  const show = (index: number) => {
    open = index;
    rows.forEach((row, i) => row.classList.toggle('is-on', i === index));
  };
  const hold = () => {
    window.clearInterval(timer);
    timer = undefined;
    root.classList.remove('is-auto');
  };
  const turn = () => {
    hold();
    if (still) return;
    void root.offsetWidth; // so the open row's line starts again
    root.classList.add('is-auto');
    timer = window.setInterval(() => show((open + 1) % rows.length), PANEL_MS);
  };

  const onRow = (e: Event) => {
    const row = (e.target as Element).closest?.<HTMLButtonElement>(
      '[data-rx-feat]',
    );
    const index = row ? rows.indexOf(row) : -1;
    if (index < 0) return;
    if (e.type === 'mouseover') pointing = true;
    if (index !== open) show(index);
    hold();
  };
  const onLeave = () => {
    pointing = false;
    turn();
  };
  const onBlur = (e: FocusEvent) => {
    const to = e.relatedTarget;
    if (to instanceof Node && root.contains(to)) return;
    if (!pointing) turn();
  };
  // A press too: Safari does not focus a button it presses.
  for (const type of ['mouseover', 'focusin', 'click']) {
    root.addEventListener(type, onRow);
  }
  root.addEventListener('mouseleave', onLeave);
  root.addEventListener('focusout', onBlur);
  show(open);
  turn();

  return () => {
    hold();
    for (const type of ['mouseover', 'focusin', 'click']) {
      root.removeEventListener(type, onRow);
    }
    root.removeEventListener('mouseleave', onLeave);
    root.removeEventListener('focusout', onBlur);
  };
}

/** The close reminder's "▲ Closes tomorrow", on the reader's calendar. */
function paintReminder(panel: HTMLElement, now: Date, zone: string): void {
  const due = $(panel, '[data-rx-close]');
  const close = due?.dataset.rxClose ? new Date(due.dataset.rxClose) : null;
  if (!due || !close || Number.isNaN(close.getTime())) return;
  const urgency = urgencyOf(daysLeft(close, now, zone));
  text(due, urgency.text);
  due.dataset.tone = urgency.tone;
}

/** The parts of a card that depend on the reader's zone. */
function paintCard(li: HTMLElement, card: ReturnType<typeof cardOf>): void {
  const pill = $(li, '[data-rx="pill"]');
  if (pill && card.pill) {
    text(pill, card.pill.text);
    pill.dataset.tone = card.pill.tone;
  }
  text($(li, '[data-rx="closes"]'), card.closes);
}

function dotButton(dot: DotView): HTMLButtonElement {
  const b = document.createElement('button');
  b.type = 'button';
  b.className = `rx-dot is-${dot.tone}`;
  b.style.setProperty('--size', `${dot.size}px`);
  b.setAttribute('aria-label', dot.label);
  b.dataset.rxDot = dot.id;
  b.dataset.caption = dot.caption;
  return b;
}

/** Each day's label, and its dots, the most investors at the bottom. */
function paintTimeline(root: HTMLElement, columns: ColumnView[]): void {
  for (const column of columns) {
    const li = $(root, `[data-rx-day="${column.offset}"]`);
    if (!li) continue;
    li.classList.toggle('is-today', column.today);
    text($(li, '[data-rx-wd]'), column.weekday);
    text($(li, '[data-rx-dn]'), column.day);
    const box = $(li, '.rx-dots');
    if (!box) continue;
    // Redrawn only where the raises changed, so a focused dot keeps its
    // focus; otherwise only its words, whose dates are the reader's.
    const drawn = $$(box, '[data-rx-dot]');
    const same =
      drawn.length === column.dots.length &&
      drawn.every((el, i) => el.dataset.rxDot === column.dots[i].id);
    if (!same) {
      box.replaceChildren(...column.dots.map(dotButton));
      continue;
    }
    drawn.forEach((el, i) => {
      attr(el, 'aria-label', column.dots[i].label);
      el.dataset.caption = column.dots[i].caption;
    });
  }
}
